import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReauth } from '@src/store/base-query';

export const usersApi = createApi({
  reducerPath: 'usersQueries',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Profile', 'Users', 'Subscriptions'],
  endpoints: (builder) => ({
    getProfile: builder.query({
      query: () => 'users/profile',
      providesTags: ['Profile'],
    }),
    getAllUsers: builder.query({
      query: ({ page = 1, limit = 20, search } = {}) => ({
        url: 'users/admin/all',
        method: 'GET',
        params: { page, limit, ...(search && { search }) },
      }),
      providesTags: ['Users'],
    }),
    getUsersStats: builder.query({
      query: () => 'users/admin/users-stats',
      providesTags: ['Users'],
    }),
    upsertUser: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `users/upsert/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Profile'],
      // Callers navigate as soon as `await upsertUser(...).unwrap()` resolves,
      // and route guards decide from the cached profile. Waiting for the tag
      // refetch leaves a window where the guard still sees the pre-write
      // profile and bounces the user back, so fold the response in directly.
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          if (!data?._id) return;

          dispatch(
            usersApi.util.updateQueryData('getProfile', undefined, (draft) => {
              Object.assign(draft, data);
            }),
          );
        } catch {
          // The invalidated Profile tag refetch is the fallback
        }
      },
    }),

    // ============= Stripe Subscriptions =============

    // Create a subscription
    createSubscription: builder.mutation({
      query: (payload) => ({
        url: 'stripe/subscriptions',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['Subscriptions', 'Profile'],
    }),

    // Get current user's active subscription
    getCurrentSubscription: builder.query({
      query: () => 'stripe/subscriptions/current',
      providesTags: ['Subscriptions'],
      keepUnusedDataFor: 300,
      transformResponse: (response) => response.data,
    }),

    // Get all subscriptions for current user
    getAllSubscriptions: builder.query({
      query: () => 'stripe/subscriptions',
      providesTags: ['Subscriptions'],
      transformResponse: (response) => response.data || [],
    }),

    // Get subscription by ID
    getSubscriptionById: builder.query({
      query: (id) => `stripe/subscriptions/${id}`,
      providesTags: (result, error, id) => [{ type: 'Subscriptions', id }],
      transformResponse: (response) => response.data,
    }),

    // Update subscription
    updateSubscription: builder.mutation({
      query: ({ subscriptionId, updates }) => ({
        url: `stripe/subscriptions/${subscriptionId}`,
        method: 'PUT',
        body: updates,
      }),
      invalidatesTags: ['Subscriptions', 'Profile'],
    }),

    // Cancel subscription
    cancelSubscription: builder.mutation({
      query: ({ subscriptionId, cancelAt, reason }) => ({
        url: `stripe/subscriptions/${subscriptionId}`,
        method: 'DELETE',
        body: { cancelAt, reason },
      }),
      invalidatesTags: ['Subscriptions', 'Profile'],
    }),

    // Resume subscription
    resumeSubscription: builder.mutation({
      query: (subscriptionId) => ({
        url: `stripe/subscriptions/${subscriptionId}/resume`,
        method: 'POST',
      }),
      invalidatesTags: ['Subscriptions', 'Profile'],
    }),

    // ============= Personalized Plan =============

    // Generate personalized health plan
    generatePersonalizedPlan: builder.mutation({
      query: () => ({
        url: 'users/generate-plan',
        method: 'POST',
      }),
      invalidatesTags: ['Profile'],
    }),

    // Regenerate personalized health plan
    regeneratePersonalizedPlan: builder.mutation({
      query: () => ({
        url: 'users/regenerate-plan',
        method: 'POST',
      }),
      invalidatesTags: ['Profile'],
    }),

    // Generate daily AI insights
    generateDailyInsights: builder.mutation({
      query: () => ({
        url: 'users/generate-daily-insights',
        method: 'POST',
      }),
      invalidatesTags: ['Profile'],
    }),

    // Regenerate daily AI insights (Admin only)
    regenerateDailyInsights: builder.mutation({
      query: () => ({
        url: 'users/regenerate-daily-insights',
        method: 'POST',
      }),
      invalidatesTags: ['Profile'],
    }),

    cancelTrial: builder.mutation({
      query: () => ({
        url: 'users/cancel-trial',
        method: 'POST',
      }),
      invalidatesTags: ['Profile'],
    }),

    deleteAccount: builder.mutation({
      query: () => ({
        url: 'users/me',
        method: 'DELETE',
      }),
    }),

    // ============= Admin Invites =============

    promoteToInvited: builder.mutation({
      query: (userId) => ({
        url: 'invites/promote',
        method: 'POST',
        body: { userId },
      }),
      invalidatesTags: ['Users'],
    }),

    revokeInvitedStatus: builder.mutation({
      query: (userId) => ({
        url: `invites/${userId}/revoke`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Users'],
    }),

    adminDeleteUser: builder.mutation({
      query: (userId) => ({
        url: `users/admin/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Users'],
    }),
  }),
});

export const {
  useGetProfileQuery,
  useGetAllUsersQuery,
  useGetUsersStatsQuery,
  useUpsertUserMutation,
  // Subscriptions
  useCreateSubscriptionMutation,
  useGetCurrentSubscriptionQuery,
  useLazyGetCurrentSubscriptionQuery,
  useGetAllSubscriptionsQuery,
  useGetSubscriptionByIdQuery,
  useUpdateSubscriptionMutation,
  useCancelSubscriptionMutation,
  useResumeSubscriptionMutation,
  // Personalized Plan
  useGeneratePersonalizedPlanMutation,
  useRegeneratePersonalizedPlanMutation,
  // Daily Insights
  useGenerateDailyInsightsMutation,
  useRegenerateDailyInsightsMutation,
  // Trial
  useCancelTrialMutation,
  // Account
  useDeleteAccountMutation,
  // Admin Invites
  usePromoteToInvitedMutation,
  useRevokeInvitedStatusMutation,
  // Admin User Management
  useAdminDeleteUserMutation,
} = usersApi;
